import { CommonModule } from '@angular/common';
import { AfterViewChecked, AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output, QueryList, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';

import { ResourceGraphNode, ResourceGraphReference } from './tenant-operations.service';

export interface ResourceGraphWorkspaceRow {
  node: ResourceGraphNode;
  depth: number;
  recordLoadState: string;
  expanded: boolean;
  branchLoading: boolean;
  dependencyReferences: ResourceGraphReference[];
  branchError: string | null;
  dependencyExpanded: boolean;
  detailPlaceholder?: boolean;
  group?: 'ownership' | 'detached';
}

export interface InspectionTrailStop {
  componentId: string;
  index: number;
  isOrigin: boolean;
  isCurrent: boolean;
}

@Component({
  selector: 'app-resource-graph-workspace',
  imports: [CommonModule],
  templateUrl: './resource-graph-workspace.component.html',
  styleUrls: ['./resource-graph-workspace.component.css'],
})
export class ResourceGraphWorkspaceComponent implements AfterViewInit, AfterViewChecked, OnChanges, OnDestroy {
  private static readonly defaultSideTraySizePx = 800;
  private static readonly minimumSideTraySizePx = 280;
  private static readonly minimumSideGraphSizePx = 384;
  private static readonly defaultFooterTraySizePx = 320;
  private static readonly minimumFooterTraySizePx = 160;
  private static readonly minimumFooterGraphSizePx = 192;
  private static readonly trayResizeHandleSizePx = 12;
  private static readonly defaultSideWorkspaceSizePx = 1400;
  private static readonly defaultFooterWorkspaceSizePx = 608;
  private static readonly sideTrayBreakpointPx = 1200;
  private static readonly sideTrayStorageKey = 'tenant-operations.resource-tray-width';
  private static readonly footerTrayStorageKey = 'tenant-operations.resource-details-footer-height';

  @Input({ required: true }) tenantId = '';
  @Input() refreshing = false;
  @Input() refreshError: string | null = null;
  @Input() rows: ResourceGraphWorkspaceRow[] = [];
  @Input() selectedNode: ResourceGraphNode | null = null;
  @Input() inspectionTrail: InspectionTrailStop[] = [];

  @Output() readonly nodeSelected = new EventEmitter<ResourceGraphNode>();
  @Output() readonly componentToggled = new EventEmitter<string>();
  @Output() readonly referenceSelected = new EventEmitter<ResourceGraphReference>();
  @Output() readonly inspectionTrailStopSelected = new EventEmitter<number>();
  @Output() readonly refreshRequested = new EventEmitter<void>();

  resourceTraySizePx = ResourceGraphWorkspaceComponent.defaultSideTraySizePx;
  isFooterTrayLayout = false;
  private usesDefaultTraySize = true;

  @ViewChild('resourceWorkspace') private resourceWorkspace?: ElementRef<HTMLElement>;
  @ViewChild('treeTable') private treeTable?: ElementRef<HTMLElement>;
  @ViewChild('connectorLayer') private connectorLayer?: ElementRef<SVGSVGElement>;
  @ViewChildren('treeControl', { read: ElementRef }) private treeControls?: QueryList<ElementRef<HTMLElement>>;

  private resizeStartPosition = 0;
  private resizeStartSizePx = 0;
  private resizingWorkspace?: HTMLElement;
  private connectorSignature = '';
  private connectorMeasurementFramesRemaining = 0;
  private connectorMeasurementFrame?: number;
  private defaultTraySizeFrame?: number;
  private renderedControlCount?: number;
  private observedTreeTable?: HTMLElement;
  private treeResizeObserver?: ResizeObserver;
  private observedTreeTableSize = '';

  private readonly resizeTray = (event: PointerEvent): void => {
    if (!this.resizingWorkspace) {
      return;
    }
    const pointerPosition = this.isFooterTrayLayout ? event.clientY : event.clientX;
    this.resourceTraySizePx = this.clampTraySize(
      this.resizeStartSizePx - (pointerPosition - this.resizeStartPosition),
      this.resizingWorkspace
    );
  };
  private readonly finishTrayResize = (): void => {
    if (!this.resizingWorkspace) {
      return;
    }
    this.persistTraySize();
    this.resizingWorkspace = undefined;
    window.removeEventListener('pointermove', this.resizeTray);
    window.removeEventListener('pointerup', this.finishTrayResize);
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tenantId']) {
      this.restoreTraySize();
    }
    if (changes['rows']) {
      this.scheduleConnectorMeasurement();
      this.scheduleDefaultSideTraySize();
    }
  }

  ngAfterViewInit(): void {
    this.updateTrayLayout();
    this.scheduleDefaultSideTraySize();
  }

  ngAfterViewChecked(): void {
    this.observeTreeTable();
    const controlCount = this.treeControls?.length ?? 0;
    if (controlCount === this.renderedControlCount) {
      return;
    }
    this.renderedControlCount = controlCount;
    if (!this.updateConnectors()) {
      this.scheduleConnectorMeasurement();
    }
  }

  private observeTreeTable(): void {
    const table = this.treeTable?.nativeElement;
    if (!table || table === this.observedTreeTable) {
      return;
    }
    this.treeResizeObserver?.disconnect();
    this.observedTreeTable = table;
    this.observedTreeTableSize = '';
    this.treeResizeObserver = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      const size = `${width}:${height}`;
      if (size === this.observedTreeTableSize) {
        return;
      }
      this.observedTreeTableSize = size;
      this.updateConnectors();
    });
    this.treeResizeObserver.observe(table);
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateTrayLayout();
    this.scheduleConnectorMeasurement();
  }

  scheduleConnectorMeasurement(): void {
    if (this.connectorMeasurementFramesRemaining > 0) {
      return;
    }
    this.connectorMeasurementFramesRemaining = 2;
    this.measureConnectorsAfterLayout();
  }

  private measureConnectorsAfterLayout(): void {
    this.connectorMeasurementFrame = requestAnimationFrame(() => {
      this.connectorMeasurementFrame = undefined;
      this.connectorMeasurementFramesRemaining--;
      const measured = this.updateConnectors();
      if (!measured && this.connectorMeasurementFramesRemaining > 0) {
        this.measureConnectorsAfterLayout();
      } else {
        this.connectorMeasurementFramesRemaining = 0;
      }
    });
  }

  private updateConnectors(): boolean {
    const table = this.treeTable?.nativeElement;
    const controls = this.treeControls?.toArray() ?? [];
    if (!table || controls.length !== this.rows.length) {
      return false;
    }

    const tableBounds = table.getBoundingClientRect();
    if (!tableBounds.width || !tableBounds.height) {
      return false;
    }
    const centers = controls.map(({ nativeElement }) => {
      const bounds = nativeElement.getBoundingClientRect();
      return {
        x: bounds.left - tableBounds.left + bounds.width / 2,
        y: bounds.top - tableBounds.top + bounds.height / 2,
      };
    });
    const ancestorIndexes: number[] = [];
    const childrenByParent = new Map<number, number[]>();
    this.rows.forEach((row, index) => {
      ancestorIndexes.length = row.depth;
      const parentIndex = row.depth > 0 ? ancestorIndexes[row.depth - 1] : undefined;
      if (parentIndex !== undefined) {
        const childIndexes = childrenByParent.get(parentIndex) ?? [];
        childIndexes.push(index);
        childrenByParent.set(parentIndex, childIndexes);
      }
      ancestorIndexes.push(index);
    });
    const paths = Array.from(childrenByParent.entries()).flatMap(([parentIndex, childIndexes]) => {
      const parent = centers[parentIndex];
      const lastChild = centers[childIndexes[childIndexes.length - 1]];
      return [
        `M ${parent.x} ${parent.y} V ${lastChild.y}`,
        ...childIndexes.map((childIndex) => `M ${parent.x} ${centers[childIndex].y} H ${centers[childIndex].x}`),
      ];
    });
    const viewBox = `0 0 ${tableBounds.width} ${tableBounds.height}`;
    const signature = `${viewBox}:${paths.join('|')}`;
    if (signature === this.connectorSignature) {
      return true;
    }
    const connectorLayer = this.connectorLayer?.nativeElement;
    if (!connectorLayer) {
      return false;
    }
    this.connectorSignature = signature;
    connectorLayer.setAttribute('viewBox', viewBox);
    const svgNamespace = 'http://www.w3.org/2000/svg';
    connectorLayer.replaceChildren(
      ...paths.map((path) => {
        const element = document.createElementNS(svgNamespace, 'path');
        element.setAttribute('d', path);
        element.setAttribute('fill', 'none');
        element.setAttribute('stroke', '#a4b6c1');
        element.setAttribute('stroke-width', '1');
        element.setAttribute('vector-effect', 'non-scaling-stroke');
        return element;
      })
    );
    return true;
  }

  ngOnDestroy(): void {
    if (this.connectorMeasurementFrame !== undefined) {
      cancelAnimationFrame(this.connectorMeasurementFrame);
    }
    if (this.defaultTraySizeFrame !== undefined) {
      cancelAnimationFrame(this.defaultTraySizeFrame);
    }
    this.treeResizeObserver?.disconnect();
    window.removeEventListener('pointermove', this.resizeTray);
    window.removeEventListener('pointerup', this.finishTrayResize);
  }

  startTrayResize(event: PointerEvent, workspace: HTMLElement): void {
    event.preventDefault();
    this.resizingWorkspace = workspace;
    this.resizeStartPosition = this.isFooterTrayLayout ? event.clientY : event.clientX;
    this.resizeStartSizePx = this.resourceTraySizePx;
    window.addEventListener('pointermove', this.resizeTray);
    window.addEventListener('pointerup', this.finishTrayResize);
  }

  resizeTrayWithKeyboard(event: KeyboardEvent, workspace: HTMLElement): void {
    const step = 16;
    let nextSize: number | null = null;
    if (event.key === (this.isFooterTrayLayout ? 'ArrowUp' : 'ArrowRight')) {
      nextSize = this.resourceTraySizePx + step;
    } else if (event.key === (this.isFooterTrayLayout ? 'ArrowDown' : 'ArrowLeft')) {
      nextSize = this.resourceTraySizePx - step;
    } else if (event.key === 'Home') {
      nextSize = this.minimumTraySizePx;
    } else if (event.key === 'End') {
      nextSize = this.maximumTraySize(workspace);
    }
    if (nextSize === null) {
      return;
    }
    event.preventDefault();
    this.resourceTraySizePx = this.clampTraySize(nextSize, workspace);
    this.persistTraySize();
  }

  resourceId(node: ResourceGraphNode): string {
    const sortKey = node.identifiers['SK'];
    if (typeof sortKey === 'string') {
      const resourceId = sortKey.split('#').at(-1);
      if (resourceId) {
        return resourceId;
      }
    }
    const [, , resourceId] = node.component_id.split('#', 3);
    return resourceId || node.component_id;
  }

  trailStopsFor(componentId: string): InspectionTrailStop[] | null {
    const stops = this.inspectionTrail.filter((stop) => stop.componentId === componentId);
    return stops.length ? stops : null;
  }

  hasOriginTrailStop(stops: InspectionTrailStop[]): boolean {
    return stops.some((stop) => stop.isOrigin);
  }

  hasCurrentTrailStop(stops: InspectionTrailStop[]): boolean {
    return stops.some((stop) => stop.isCurrent);
  }


  get minimumTraySizePx(): number {
    return this.isFooterTrayLayout
      ? ResourceGraphWorkspaceComponent.minimumFooterTraySizePx
      : ResourceGraphWorkspaceComponent.minimumSideTraySizePx;
  }

  maximumTraySize(workspace: HTMLElement): number {
    const workspaceSize = this.isFooterTrayLayout
      ? workspace.clientHeight || ResourceGraphWorkspaceComponent.defaultFooterWorkspaceSizePx
      : workspace.clientWidth || ResourceGraphWorkspaceComponent.defaultSideWorkspaceSizePx;
    const minimumGraphSize = this.isFooterTrayLayout
      ? ResourceGraphWorkspaceComponent.minimumFooterGraphSizePx
      : ResourceGraphWorkspaceComponent.minimumSideGraphSizePx;
    return Math.max(
      this.minimumTraySizePx,
      workspaceSize - minimumGraphSize - ResourceGraphWorkspaceComponent.trayResizeHandleSizePx
    );
  }

  private clampTraySize(size: number, workspace: HTMLElement): number {
    return Math.min(Math.max(size, this.minimumTraySizePx), this.maximumTraySize(workspace));
  }

  private updateTrayLayout(): void {
    const workspace = this.resourceWorkspace?.nativeElement;
    const workspaceWidth = workspace?.clientWidth || window.innerWidth;
    const isFooterTrayLayout = workspaceWidth <= ResourceGraphWorkspaceComponent.sideTrayBreakpointPx;
    if (isFooterTrayLayout === this.isFooterTrayLayout) {
      return;
    }
    this.isFooterTrayLayout = isFooterTrayLayout;
    this.restoreTraySize();
    this.scheduleDefaultSideTraySize();
  }

  private scheduleDefaultSideTraySize(): void {
    if (this.defaultTraySizeFrame !== undefined) {
      return;
    }
    this.defaultTraySizeFrame = requestAnimationFrame(() => {
      this.defaultTraySizeFrame = undefined;
      this.applyDefaultSideTraySize();
    });
  }

  private applyDefaultSideTraySize(): void {
    if (this.isFooterTrayLayout || !this.usesDefaultTraySize) {
      return;
    }
    const workspace = this.resourceWorkspace?.nativeElement;
    const treeTable = this.treeTable?.nativeElement;
    if (!workspace || !treeTable || !workspace.clientWidth) {
      return;
    }
    const graphSize = Math.max(
      ResourceGraphWorkspaceComponent.minimumSideGraphSizePx,
      treeTable.scrollWidth + 32
    );
    const maximumDefaultSize = Math.max(
      ResourceGraphWorkspaceComponent.minimumSideTraySizePx,
      workspace.clientWidth - graphSize - ResourceGraphWorkspaceComponent.trayResizeHandleSizePx
    );
    this.resourceTraySizePx = Math.min(ResourceGraphWorkspaceComponent.defaultSideTraySizePx, maximumDefaultSize);
  }

  private restoreTraySize(): void {
    try {
      const storageKey = this.isFooterTrayLayout
        ? ResourceGraphWorkspaceComponent.footerTrayStorageKey
        : ResourceGraphWorkspaceComponent.sideTrayStorageKey;
      const storedSize = Number(window.localStorage.getItem(storageKey));
      const defaultSize = this.isFooterTrayLayout
        ? ResourceGraphWorkspaceComponent.defaultFooterTraySizePx
        : ResourceGraphWorkspaceComponent.defaultSideTraySizePx;
      const defaultWorkspaceSize = this.isFooterTrayLayout
        ? ResourceGraphWorkspaceComponent.defaultFooterWorkspaceSizePx
        : ResourceGraphWorkspaceComponent.defaultSideWorkspaceSizePx;
      const minimumGraphSize = this.isFooterTrayLayout
        ? ResourceGraphWorkspaceComponent.minimumFooterGraphSizePx
        : ResourceGraphWorkspaceComponent.minimumSideGraphSizePx;
      if (
        Number.isFinite(storedSize) &&
        storedSize >= this.minimumTraySizePx &&
        storedSize <= defaultWorkspaceSize - minimumGraphSize
      ) {
        this.resourceTraySizePx = storedSize;
        this.usesDefaultTraySize = false;
      } else {
        this.resourceTraySizePx = defaultSize;
        this.usesDefaultTraySize = true;
      }
    } catch {
      this.resourceTraySizePx = this.isFooterTrayLayout
        ? ResourceGraphWorkspaceComponent.defaultFooterTraySizePx
        : ResourceGraphWorkspaceComponent.defaultSideTraySizePx;
      this.usesDefaultTraySize = true;
    }
  }

  private persistTraySize(): void {
    this.usesDefaultTraySize = false;
    try {
      const storageKey = this.isFooterTrayLayout
        ? ResourceGraphWorkspaceComponent.footerTrayStorageKey
        : ResourceGraphWorkspaceComponent.sideTrayStorageKey;
      window.localStorage.setItem(storageKey, String(this.resourceTraySizePx));
    } catch {
      // Local preference storage is optional for the Operational View.
    }
  }
}
